import { z } from 'zod';

/**
 * Typed socket errors.
 *
 * A failed guard emits one of these rather than throwing (§9.4), so a client
 * always has a machine-readable reason. The codes mirror the HTTP problem
 * types in `apps/api/src/http/errors.ts` where they overlap.
 */
export const socketErrorCodeSchema = z.enum([
  'unauthenticated',
  'forbidden',
  'not-found',
  'validation-failed',
  'name-taken',
  'name-rejected',
  'session-closed',
  'rate-limited',
  'internal',
]);

export type SocketErrorCode = z.infer<typeof socketErrorCodeSchema>;

export const socketErrorSchema = z.object({
  code: socketErrorCodeSchema,
  message: z.string(),
});

export type SocketError = z.infer<typeof socketErrorSchema>;

/**
 * Result of an acknowledged request.
 *
 * A discriminated union rather than a thrown exception: an error crossing a
 * socket boundary has to survive serialisation, and a client that forgets to
 * handle the failure branch is then a type error rather than a silent hang.
 */
export type Ack<T> = { ok: true; data: T } | { ok: false; error: SocketError };

export const ackOk = <T>(data: T): Ack<T> => ({ ok: true, data });

export const ackError = (code: SocketErrorCode, message: string): Ack<never> => ({
  ok: false,
  error: { code, message },
});
